<template>
  <div class="container counterparty-container">
    <base-nav title="Контрагенты">
      <base-button
        v-if="counterparties && counterparties.length > 0"
        @click="exportToExcel"
      >
        Экспорт в Excel
      </base-button>
      <base-primary-button
        icon="addSquare"
        @click="goToCounterpartyCreate"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Counterparty"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="
        !dataIsLoading && counterparties && counterparties.length > 0
      "
      :pagination-meta="pagination"
    />

    <!-- Переключение вида и действия -->
    <TableToolbar
      v-if="!dataIsLoading && counterparties.length > 0"
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? dropdownItems : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        counterparties &&
        counterparties.length > 0 &&
        currentView == 'table'
      "
      class="counterparty-center"
    >
      <TableComponent
        :data="counterparties"
        :columns="tableColumns"
        :ready="!dataIsLoading && tableColumns.length > 0"
        selectable
        @selection-change="onSelectionChange"
        @row-middle-click="
          openRouteInNewTab({
            name: 'CounterpartyDetails',
            params: { id: $event.id },
          })
        "
        @row-click="goToCounterpartyDetails"
      />
    </div>

    <!-- Карточный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        counterparties &&
        counterparties.length > 0 &&
        currentView === 'cards'
      "
      class="counterparty-block"
    >
      <ul class="counterparty-list">
        <li
          class="card counterparty-item"
          v-for="(counterparty, index) in counterparties"
          :key="index"
          @click="goToCounterpartyDetails(counterparty.id)"
        >
          <div class="item-title">
            <h3 class="item-title-name" v-if="counterparty">
              {{ counterparty.name }}
            </h3>
            <div class="info-item" v-if="counterparty.source_type">
              <span
                class="source-type-badge"
                :class="`source-type--${counterparty.source_type}`"
              >
                {{ sourceTypeLabel(counterparty.source_type) }}
              </span>
            </div>
          </div>

          <div class="item-center" v-if="counterparty.inn">
            <span class="value">ИНН: {{ counterparty.inn }}</span>
          </div>

          <div
            class="item-bottom"
            v-if="counterparty.created_at || counterparty.updated_at"
          >
            <MetaTime
              :created-at="counterparty.created_at"
              :updated-at="counterparty.updated_at"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Пагинация -->
    <pagination-component
      v-if="
        pagination.pages > 1 &&
        !dataIsLoading &&
        tableColumns.length > 0
      "
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="currentRouteQuery"
    />

    <div
      class="no-data"
      v-if="!dataIsLoading && counterparties.length == 0"
    >
      Список контрагентов пуст
    </div>

    <!-- Модальное окно настроек -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Counterparty"
        record-type="Counterparty"
        table-name="counterparties"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>
  </div>
</template>

<script>
import counterpartiesApi from "@/services/api/counterpartiesApi";

import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";

import { getCompanyId } from "@/utils/userUtils";
import { exportToExcel } from "@/utils/fileUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

import FilterBlock from "@/components/Filter/FilterBlock.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableComponent from "@/components/TableComponent.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";

import searchFields from "@/config/filters/counterparty/counterparties.json";

export default {
  name: "CounterpartiesPage",
  components: {
    FilterBlock,
    PaginationComponent,
    MetaTime,
    MetaResultCount,
    TableComponent,
    TableSettingsModal,
    TableToolbar,
  },
  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: true,
      currentView: "table",
      showSettingsModal: false,

      counterparties: [],
      tableColumns: [],
      selectedCounterparties: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },
      searchFields,

      dropdownItems: [
        {
          id: "enable",
          label: "Показать",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Скрыть",
          isDanger: true,
          action: this.disableSelected,
        },
      ],

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.getCounterpartiesRequest();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedCounterparties = [];
      }
    },
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },
  },
  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Counterparty",
            "counterparties"
          );

        this.tableColumns = columns;
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы контрагентов",
          error
        );
        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    sourceTypeLabel(type) {
      const labels = {
        manual: "Создан вручную",
        bank_statement: "Импортирован из банковских выписок",
      };
      return labels[type] || type;
    },

    async getCounterpartiesRequest() {
      const page = getCurrentPageFromUrl(this.$route);

      try {
        this.dataIsLoading = true;

        const params = {
          page: page,
          ...filtersToApiParams(
            this.appliedFilters,
            this.searchFields
          ),
        };

        const response = await counterpartiesApi.index(
          this.companyId,
          params
        );

        this.counterparties =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            response.data.result,
            this.tableColumns
          );

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };

        this.selectedCounterparties = [];
      } catch (error) {
        console.error("Ошибка при загрузке контрагентов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedCounterparties = [];
    },

    goToCounterpartyCreate() {
      this.$router.push({ name: "CounterpartyCreate" });
    },

    goToCounterpartyDetails(counterparty) {
      const counterpartyId = counterparty.id || counterparty;
      this.$router.push({
        name: "CounterpartyDetails",
        params: { id: counterpartyId },
      });
    },

    onSelectionChange(selected) {
      this.selectedCounterparties = selected;
    },

    async enableSelected() {
      if (this.selectedCounterparties.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного контрагента"
        );
        return;
      }

      try {
        const enablePromises = this.selectedCounterparties.map(
          (counterparty) =>
            counterpartiesApi.enable(this.companyId, counterparty.id)
        );

        await Promise.all(enablePromises);

        this.notificationStore.showSuccess(
          `Успешно включено ${this.selectedCounterparties.length} контрагентов`
        );

        await this.getCounterpartiesRequest();
      } catch (error) {
        console.error("Ошибка при включении контрагентов:", error);
      }
    },

    async disableSelected() {
      if (this.selectedCounterparties.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного контрагента"
        );
        return;
      }

      try {
        const disablePromises = this.selectedCounterparties.map(
          (counterparty) =>
            counterpartiesApi.disable(this.companyId, counterparty.id)
        );

        await Promise.all(disablePromises);

        this.notificationStore.showSuccess(
          `Успешно отключено ${this.selectedCounterparties.length} контрагентов`
        );

        await this.getCounterpartiesRequest();
      } catch (error) {
        console.error("Ошибка при отключении контрагентов:", error);
      }
    },

    async exportToExcel() {
      try {
        const search_params = filtersToApiParams(
          this.appliedFilters,
          this.searchFields
        );

        const response = await counterpartiesApi.export(
          this.companyId,
          search_params
        );

        exportToExcel(response, "counterparties.xlsx");
      } catch (error) {
        console.error("Ошибка при экспорте в Excel:", error);
      }
    },

    openRouteInNewTab,
  },
  async created() {
    this.updateFiltersFromUrl();

    await this.loadTableConfig();
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getCounterpartiesRequest();
  },
};
</script>

<style scoped>
.counterparty-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--base-out-indent);
  gap: 15px;
}

.header-actions {
  display: flex;
  gap: 15px;
}

.counterparty-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.counterparty-item {
  position: relative;
  cursor: pointer;
}

.counterparty-item:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.item-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.item-title-name {
  max-width: 60%;
}

.item-center {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: var(--base-in-indent);
}

.item-bottom {
  display: flex;
  flex-direction: column;
  margin-top: var(--base-in-indent);
}

.source-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.source-type--manual {
  background: #dbeafe;
  color: var(--color-blue);
}

.source-type--bank_statement {
  background-color: #d1fae5;
  color: #059669;
}

.item-switcher-box {
  position: absolute;
  top: 20px;
  right: 20px;
}

</style>
